import { useEffect, useRef, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import AlbumCover from './AlbumCover';
import GlitchText from './effects/GlitchText';
import PlayButtonFlame from './effects/PlayButtonFlame';
import PlayButtonExplosion from './effects/PlayButtonExplosion';
import LikeButton from './controls/LikeButton';
import { PlayIcon, PauseIcon, DownloadIcon } from './icons';
import { findAlbum } from '../data/albums';
import { usePlayer, formatTime } from '../player/PlayerContext';

function hexToSoft(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 'rgba(255, 90, 31, 0.35)';
  const [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16));
  return `rgba(${r}, ${g}, ${b}, 0.35)`;
}

export default function AlbumPage() {
  const { id } = useParams();
  const album = findAlbum(id);
  const { currentAlbum, isPlaying, playAlbumById, togglePlay, duration } = usePlayer();

  const [explosionTrigger, setExplosionTrigger] = useState(0);
  const [shakeKey, setShakeKey] = useState(0);
  const shakeTimeout = useRef(null);

  useEffect(() => () => { if (shakeTimeout.current) clearTimeout(shakeTimeout.current); }, []);

  if (!album) return <Navigate to="/" replace />;

  const isCurrent = currentAlbum?.id === album.id;
  const isThisPlaying = isCurrent && isPlaying;

  const handlePlay = () => {
    setExplosionTrigger((n) => n + 1);
    setShakeKey((n) => n + 1);
    if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
    shakeTimeout.current = setTimeout(() => setShakeKey(0), 320);
    if (isCurrent) togglePlay();
    else playAlbumById(album.id);
  };

  const style = {
    '--album-accent': album.accent,
    '--album-accent-soft': hexToSoft(album.accent),
  };

  return (
    <div style={style}>
      <div className="album-hero">
        <AlbumCover album={album} className="album-hero-cover" />
        <div>
          <div className="album-hero-eyebrow">Single Album</div>
          <h1 className="album-hero-title">
            <GlitchText>{album.title}</GlitchText>
          </h1>
          <div className="album-hero-meta">
            <span>{album.artist}</span>
            <span className="dot">·</span>
            <span>{album.genre}</span>
            <span className="dot">·</span>
            <span>{album.year}</span>
            <span className="dot">·</span>
            <span>1 track</span>
          </div>
        </div>
      </div>

      <div className="album-actions">
        <span className="play-primary-wrap">
          <PlayButtonFlame active={isThisPlaying} />
          <PlayButtonExplosion trigger={explosionTrigger} />
          <button
            key={shakeKey}
            className={`play-primary ${shakeKey ? 'shake' : ''}`}
            onClick={handlePlay}
            aria-label={isThisPlaying ? 'Pause' : 'Play'}
          >
            {isThisPlaying ? <PauseIcon size={28} /> : <PlayIcon size={28} />}
          </button>
        </span>
        <LikeButton albumId={album.id} size={30} />
        <a
          className="album-download-btn"
          href={album.track.src}
          download={album.track.title + '.mp3'}
          aria-label={`Κατέβασμα ${album.title}`}
          onClick={(e) => e.stopPropagation()}
        >
          <DownloadIcon size={22} />
        </a>
      </div>

      <div className="tracklist">
        <div className="tracklist-header">
          <span className="track-index">#</span>
          <span>Τίτλος</span>
          <span className="track-duration">Διάρκεια</span>
          <span />
        </div>
        <div
          className={`track-row ${isCurrent ? 'playing' : ''}`}
          onClick={handlePlay}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePlay(); } }}
        >
          <span className="track-index">1</span>
          <span className="track-title">{album.track.title}</span>
          <span className="track-duration">
            {isCurrent && duration ? formatTime(duration) : '—:—'}
          </span>
          <a
            className="track-download-btn"
            href={album.track.src}
            download={album.track.title + '.mp3'}
            aria-label={`Κατέβασμα ${album.track.title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <DownloadIcon size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}
